import React from 'react';
import { renderMentionTextNodes } from '../mentions/chatMentionUtils';
import ChatImageGroupBubble from '../composer/ChatImageGroupBubble';
import { isPdfPreviewable, usePdfPreview } from '../../pdf/PdfPreviewProvider';
import '../../../../css/common/chat/ChatMessageContent.css';

const hasContent = (value) => String(value ?? '').trim().length > 0;
const noop = () => {};

// §4.1.7: 単一画像のアスペクト比事前確保。image_width/height が有効なら
// インライン aspect-ratio を返し、ロード完了による高さ変動(レイアウトシフト)を防ぐ。
// CSS ファイルは触らず、寸法不明時は undefined を返して従来描画のまま。
const buildSingleImageAspectStyle = (width, height) => {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
  return { aspectRatio: `${w} / ${h}` };
};

const renderAnchorAction = ({ href, className, ariaLabel, title, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={className}
    aria-label={ariaLabel}
    title={title}
  >
    {children}
  </a>
);

const renderButtonAction = ({ onClick, className, ariaLabel, title, children }) => (
  <button
    type="button"
    className={className}
    onClick={onClick}
    aria-label={ariaLabel}
    title={title}
  >
    {children}
  </button>
);

const renderAction = ({
  onClick = null,
  href = '',
  className = '',
  ariaLabel = '',
  title = '',
  children = null,
}) => {
  if (typeof onClick === 'function') {
    return renderButtonAction({ onClick, className, ariaLabel, title, children });
  }

  if (hasContent(href)) {
    return renderAnchorAction({ href, className, ariaLabel, title, children });
  }

  return null;
};

const ChatMessageContent = ({
  format = 'text',
  text = '',
  textNode = null,
  mentions = [],
  onMentionClick = null,
  imageSrc = '',
  imageAlt = 'メッセージ画像',
  imageWidth = null,
  imageHeight = null,
  onImageClick = null,
  onImageDownload = null,
  imageDownloadHref = '',
  imageDownloadIconClass = 'fa-download',
  imageDownloadAriaLabel = 'ダウンロード',
  imageDownloadTitle = 'ダウンロード',
  imageMeta = '',
  imageGroupItems = [],
  onImageGroupOpen = null,
  imageUnavailableText = '画像を表示できません。',
  fileUrl = '',
  fileName = '',
  fileFallbackName = 'ファイルを開く',
  fileVariant = 'simple',
  fileCanDownload = null,
  onFileAction = null,
  fileIconClass = 'fa-file-arrow-down',
  fileVisualKind = 'generic',
  fileIconAriaLabel = 'ファイルをダウンロード',
  fileActionTitle = 'ダウンロード',
  fileMeta = '',
  fileDisabledText = 'ファイルを開けません。',
}) => {
  const pdfPreview = usePdfPreview();
  if (format === 'image') {
    if (Array.isArray(imageGroupItems) && imageGroupItems.length > 1) {
      return (
        <>
          <ChatImageGroupBubble
            items={imageGroupItems}
            onOpen={typeof onImageGroupOpen === 'function' ? onImageGroupOpen : noop}
            unavailableText={imageUnavailableText}
          />
          {hasContent(imageMeta) ? (
            <div className="download-row chat-message-content__downloadRow">
              <span className="expire-note-inline chat-message-content__metaText">{imageMeta}</span>
            </div>
          ) : null}
        </>
      );
    }

    if (hasContent(imageSrc)) {
      const showImageDownload = typeof onImageDownload === 'function' || hasContent(imageDownloadHref);
      const showImageMeta = hasContent(imageMeta);
      return (
        <>
          <img
            src={imageSrc}
            alt={imageAlt}
            loading="lazy"
            style={buildSingleImageAspectStyle(imageWidth, imageHeight)}
            className={`chat-message-content__image${
              typeof onImageClick === 'function' ? ' chat-message-content__image--interactive' : ''
            }`}
            onClick={typeof onImageClick === 'function' ? onImageClick : undefined}
          />
          {showImageDownload || showImageMeta ? (
            <div className="download-row chat-message-content__downloadRow">
              {renderAction({
                onClick: onImageDownload,
                href: imageDownloadHref,
                className: 'download-icon-btn chat-message-content__downloadButton',
                ariaLabel: imageDownloadAriaLabel,
                title: imageDownloadTitle,
                children: <i className={`fa-solid ${imageDownloadIconClass}`} aria-hidden="true"></i>,
              })}
              {showImageMeta ? (
                <span className="expire-note-inline chat-message-content__metaText">{imageMeta}</span>
              ) : null}
            </div>
          ) : null}
        </>
      );
    }

    return (
      <span className="image-expired chat-message-content__expired chat-message-content__expired--stable">
        <i className="fa-solid fa-image" aria-hidden="true"></i>
        <span className="chat-message-content__expiredText">{imageUnavailableText}</span>
      </span>
    );
  }

  if (format === 'file') {
    const resolvedFileName = fileName || fileFallbackName;
    const isPdf = fileVisualKind === 'pdf' || isPdfPreviewable({ fileName: resolvedFileName, url: fileUrl });
    const previewPdf = isPdf && hasContent(fileUrl) && typeof pdfPreview?.openPreview === 'function'
      ? () => pdfPreview.openPreview({ url: fileUrl, fileName: resolvedFileName })
      : null;
    const effectiveFileAction = previewPdf || onFileAction;
    const effectiveFileActionTitle = previewPdf ? 'プレビュー' : fileActionTitle;
    const canDownload =
      fileCanDownload == null ? typeof effectiveFileAction === 'function' || hasContent(fileUrl) : Boolean(fileCanDownload);

    if (fileVariant === 'rich') {
      if (canDownload) {
        const fileActionLabel = `${resolvedFileName}を${effectiveFileActionTitle || '開く'}`;
        return (
          renderAction({
            onClick: effectiveFileAction,
            href: fileUrl,
            className: 'file-message chat-message-content__file chat-message-content__fileAction',
            ariaLabel: fileActionLabel || fileIconAriaLabel,
            title: effectiveFileActionTitle,
            children: (
              <>
                <div className={`file-message__icon chat-message-content__fileIcon chat-message-content__fileIcon--${fileVisualKind}`} aria-hidden="true">
                  <i className={`fa-solid ${fileIconClass}`} aria-hidden="true" />
                </div>
                <div className="file-message__body chat-message-content__fileBody">
                  <span className="file-link file-message__name chat-message-content__fileName">
                    <span className="file-name">{resolvedFileName}</span>
                  </span>
                  {hasContent(fileMeta) ? (
                    <div className="file-message__meta chat-message-content__metaText">{fileMeta}</div>
                  ) : null}
                </div>
              </>
            ),
          })
        );
      }

      return (
        <div className="file-message file-message--muted chat-message-content__file chat-message-content__file--muted">
          <div className={`file-message__icon chat-message-content__fileIcon chat-message-content__fileIcon--${fileVisualKind}`} aria-hidden="true">
            <i className={`fa-solid ${fileIconClass}`}></i>
          </div>
          <div className="file-message__body chat-message-content__fileBody">
            <span className="file-message__name file-message__name--disabled chat-message-content__fileName">
              {fileDisabledText}
            </span>
          </div>
        </div>
      );
    }

    const simpleAction = renderAction({
      onClick: effectiveFileAction,
      href: fileUrl,
      className: 'file-link file-message__name chat-message-content__fileName',
      ariaLabel: `${resolvedFileName}を${effectiveFileActionTitle || '開く'}`,
      title: effectiveFileActionTitle,
      children: resolvedFileName,
    });
    if (simpleAction) {
      return simpleAction;
    }

    return (
      <span className="file-message__name file-message__name--disabled chat-message-content__fileName chat-message-content__fileUnavailable">
        {fileDisabledText}
      </span>
    );
  }

  if (textNode != null) {
    return textNode;
  }

  return <span>{renderMentionTextNodes({ text, mentions, onMentionClick })}</span>;
};

export default ChatMessageContent;
